import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus, Trash2, Mail, MessageSquare, Bell, Tag, Megaphone, CheckSquare, Clock, AlertTriangle, GripVertical, Braces, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { automationsApi, pipelineApi, usersApi, chatApi, marketingApi, formsApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";
import { toast } from "sonner";

const SYSTEM_BOARDS = [
  { id: "sales", name: "Sales (Estimates)" },
  { id: "jobs", name: "Jobs" },
];

// Platform Console's own sales pipeline — the only board where the "A demo is
// booked" trigger applies (mirrors backend PLATFORM_BOARD_ID).
const PLATFORM_BOARD_ID = "platform-sales";

// New granular job/estimate/invoice system events (tenant only — never shown
// in the Platform Console). Mirrors backend PIPELINE_TRIGGER_EVENTS /
// AUTOMATION_SYSTEM_EVENTS in services/pipeline_triggers.py.
const JOB_SYSTEM_EVENTS = [
  { value: "job.scheduled", label: "Job is scheduled" },
  { value: "job.enroute", label: "Technician is en route" },
  { value: "job.onsite", label: "Technician is on site" },
  { value: "job.work_started", label: "Work has started on the job" },
  { value: "job.completed", label: "Job is completed" },
  { value: "job.cancelled", label: "Job is cancelled" },
  { value: "job.rescheduled", label: "Job is rescheduled" },
  { value: "job.has_unsold_estimate", label: "Job has an unsold estimate", days: true },
  { value: "job.has_sold_estimate", label: "Job has a sold estimate" },
  { value: "job.followup_due", label: "Follow-up is due after job completion", days: true },
];
const SALES_SYSTEM_EVENTS = [
  { value: "estimate.expired", label: "Estimate expires unsigned" },
  { value: "invoice.sent", label: "Invoice is sent" },
  { value: "invoice.paid", label: "Invoice is paid" },
];
const ALL_SYSTEM_EVENTS = [...JOB_SYSTEM_EVENTS, ...SALES_SYSTEM_EVENTS];

const ACTION_DEFS = {
  send_email: { label: "Send Email", icon: Mail, color: "text-blue-600 bg-blue-50 border-blue-200" },
  send_sms: { label: "Send SMS", icon: MessageSquare, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  notify_team: { label: "Notify Team", icon: Bell, color: "text-amber-600 bg-amber-50 border-amber-200" },
  set_tag: { label: "Add / Set Tag", icon: Tag, color: "text-violet-600 bg-violet-50 border-violet-200" },
  start_campaign: { label: "Start Campaign", icon: Megaphone, color: "text-pink-600 bg-pink-50 border-pink-200" },
  create_task: { label: "Create Task", icon: CheckSquare, color: "text-slate-600 bg-slate-100 border-slate-200" },
};

const inputCls = "w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400";
const labelCls = "block text-[12px] font-semibold text-slate-700 mb-1";

const minutesToParts = (m) => {
  m = Number(m) || 0;
  if (m === 0) return { val: 0, unit: "minutes" };
  if (m % 1440 === 0) return { val: m / 1440, unit: "days" };
  if (m % 60 === 0) return { val: m / 60, unit: "hours" };
  return { val: m, unit: "minutes" };
};
const partsToMinutes = (val, unit) => {
  const v = Number(val) || 0;
  return unit === "days" ? v * 1440 : unit === "hours" ? v * 60 : v;
};

let _uid = 0;
const newActionId = () => `act-ui-${++_uid}-${Math.random().toString(36).slice(2, 6)}`;

export default function AutomationBuilder({ initial, embedded = false, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [name, setName] = useState(initial?.name || "");
  const [board, setBoard] = useState(initial?.board || (embedded ? PLATFORM_BOARD_ID : "sales"));
  const [stageId, setStageId] = useState(initial?.stageId || "");
  const [trigger, setTrigger] = useState(initial?.trigger || "stage_enter");
  const [idleHours, setIdleHours] = useState(initial?.triggerConfig?.idleHours || 24);
  const [systemEvent, setSystemEvent] = useState(initial?.triggerConfig?.event || "job.completed");
  const [sysDays, setSysDays] = useState(initial?.triggerConfig?.days || 3);
  const [enabled, setEnabled] = useState(initial?.enabled !== false);
  const [actions, setActions] = useState(
    (initial?.actions || []).map((a) => ({ ...a, id: a.id || newActionId() }))
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    pipelineApi.pipelines().then(setPipelines).catch(() => {});
    usersApi.list().then((u) => setUsers(u.map((x) => ({ id: x.id, name: x.name || x.email })))).catch(() => {});
    chatApi.conversations().then((cs) => setChannels(cs.filter((c) => c.type === "channel"))).catch(() => {});
    marketingApi.listCampaigns().then(setCampaigns).catch(() => {});
    marketingApi.listEmailTemplates().then(setTemplates).catch(() => {});
  }, []);

  useEffect(() => {
    if (!board) return;
    pipelineApi.stages(board).then((st) => {
      setStages(st);
      if (!st.find((s) => s.id === stageId)) setStageId(st[0]?.id || "");
    }).catch(() => setStages([]));
    if (board !== PLATFORM_BOARD_ID) {
      setTrigger((t) => (t === "demo_booked" ? "stage_enter" : t));
    }
    if (embedded) {
      setTrigger((t) => (t === "system_event" ? "stage_enter" : t));
    }
  }, [board, embedded]);

  const boardOptions = useMemo(() => {
    const customPipelines = pipelines.filter((p) => !p.isSystem).map((p) => ({ id: p.id, name: p.name }));
    const merged = embedded ? customPipelines : [...SYSTEM_BOARDS, ...customPipelines];
    const seen = new Set();
    return merged.filter((b) => (seen.has(b.id) ? false : (seen.add(b.id), true)));
  }, [pipelines, embedded]);

  // Job events for the Jobs board (or a custom pipeline); Sales events for the
  // Sales board (or a custom pipeline) — a custom pipeline can represent
  // either workflow, so it gets both.
  const systemEventOptions = useMemo(() => {
    if (board === "jobs") return JOB_SYSTEM_EVENTS;
    if (board === "sales") return SALES_SYSTEM_EVENTS;
    return ALL_SYSTEM_EVENTS;
  }, [board]);
  useEffect(() => {
    if (!systemEventOptions.find((o) => o.value === systemEvent)) {
      setSystemEvent(systemEventOptions[0]?.value || "");
    }
  }, [systemEventOptions]);
  const systemEventNeedsDays = !!systemEventOptions.find((o) => o.value === systemEvent)?.days;

  const savedEmails = useMemo(() => ([
    ...campaigns.map((c) => ({ key: `campaign:${c.id}`, kind: "campaign", id: c.id, name: c.name, hasSubject: !!(c.subject && c.subject.trim()) })),
    ...templates.map((t) => ({ key: `template:${t.id}`, kind: "template", id: t.id, name: t.name, hasSubject: !!(t.subject && t.subject.trim()) })),
  ]), [campaigns, templates]);

  const addAction = (type) => {
    setActions((prev) => [...prev, { id: newActionId(), type, delayMinutes: 0, config: defaultConfig(type) }]);
    setAddOpen(false);
  };
  const updateAction = (id, patch) =>
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  const updateConfig = (id, patch) =>
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, config: { ...a.config, ...patch } } : a)));
  const removeAction = (id) => setActions((prev) => prev.filter((a) => a.id !== id));

  const save = async () => {
    if (!name.trim()) return setError("Give your automation a name");
    if (!stageId) return setError("Choose a stage");
    if (trigger === "system_event" && !systemEvent) return setError("Choose a system event");
    if (!actions.length) return setError("Add at least one action");
    for (const a of actions) {
      if (a.type === "send_email" && a.config?.sourceType && a.config?.sourceId) {
        const se = savedEmails.find((s) => s.kind === a.config.sourceType && s.id === a.config.sourceId);
        if (se && !se.hasSubject && !(a.config.subject || "").trim()) {
          return setError(`Add a subject for the selected email "${se.name}"`);
        }
      }
    }
    setBusy(true);
    setError("");
    const payload = {
      name: name.trim(),
      board,
      stageId,
      trigger,
      triggerConfig:
        trigger === "idle_in_stage" ? { idleHours: Number(idleHours) || 24 }
        : trigger === "system_event" ? { event: systemEvent, ...(systemEventNeedsDays ? { days: Number(sysDays) || 3 } : {}) }
        : {},
      actions: actions.map((a) => ({
        type: a.type,
        delayMinutes: Number(a.delayMinutes) || 0,
        config: normalizeConfig(a.type, a.config),
      })),
      enabled,
    };
    try {
      const saved = isEdit
        ? await automationsApi.update(initial.id, payload)
        : await automationsApi.create(payload);
      onSaved(saved);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save automation");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-stretch justify-end bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-slate-50 w-full max-w-2xl h-full overflow-y-auto shadow-2xl flex flex-col" data-testid="automation-builder">
        <div className="px-6 py-4 border-b border-slate-200 bg-white flex items-center justify-between sticky top-0 z-10">
          <div className="text-[15px] font-semibold text-slate-900">{isEdit ? "Edit automation" : "New automation"}</div>
          <button onClick={onClose} data-testid="builder-close" title="Close" className="h-10 w-10 rounded-lg bg-slate-800 hover:bg-slate-900 shadow-sm flex items-center justify-center shrink-0">
            <X className="h-7 w-7 shrink-0" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
          </button>
        </div>

        <div className="flex-1 p-6 space-y-5">
          {/* Name + enabled */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-end gap-3">
            <div className="flex-1">
              <label className={labelCls}>Automation name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="builder-name" placeholder="e.g. New Lead Welcome" className={inputCls} />
            </div>
            <div className="flex items-center gap-2 pb-1">
              <span className="text-[12px] font-medium text-slate-600">{enabled ? "On" : "Off"}</span>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="builder-enabled" />
            </div>
          </div>

          {/* Assignment */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="text-[12.5px] font-bold text-slate-800 uppercase tracking-wide">Apply to</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Pipeline</label>
                <select value={board} onChange={(e) => setBoard(e.target.value)} data-testid="builder-board" className={inputCls}>
                  {boardOptions.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Stage</label>
                <select value={stageId} onChange={(e) => setStageId(e.target.value)} data-testid="builder-stage" className={inputCls}>
                  {stages.length === 0 && <option value="">No stages</option>}
                  {stages.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Trigger */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="text-[12.5px] font-bold text-slate-800 uppercase tracking-wide">When (trigger)</div>
            <div className="space-y-2">
              <TriggerOption
                active={trigger === "stage_enter"}
                onClick={() => setTrigger("stage_enter")}
                testid="trigger-stage-enter"
                title="A card enters this stage"
                desc="Fires the moment a card lands in the stage above."
              />
              <TriggerOption
                active={trigger === "idle_in_stage"}
                onClick={() => setTrigger("idle_in_stage")}
                testid="trigger-idle"
                title="A card is idle in this stage"
                desc="Fires once a card sits in the stage without moving."
              >
                {trigger === "idle_in_stage" && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[12px] text-slate-500">for</span>
                    <input type="number" min="1" value={idleHours} onChange={(e) => setIdleHours(e.target.value)} data-testid="trigger-idle-hours" className="w-20 h-8 px-2 rounded-md border border-slate-200 text-[13px] text-slate-900 bg-white" />
                    <span className="text-[12px] text-slate-500">hours</span>
                  </div>
                )}
              </TriggerOption>
              {board === PLATFORM_BOARD_ID && (
                <TriggerOption
                  active={trigger === "demo_booked"}
                  onClick={() => setTrigger("demo_booked")}
                  testid="trigger-demo-booked"
                  title="A demo is booked"
                  desc="Fires when a visitor books a demo on the website — carries the booker's name, email, booked date/time, and meeting duration."
                />
              )}
              {!embedded && (
                <TriggerOption
                  active={trigger === "system_event"}
                  onClick={() => setTrigger("system_event")}
                  testid="trigger-system-event"
                  title="A system event happens"
                  desc="Fires on a specific job/estimate/invoice event, for a card currently in the stage above."
                >
                  {trigger === "system_event" && (
                    <div className="space-y-2 mt-2">
                      <select
                        value={systemEvent}
                        onChange={(e) => setSystemEvent(e.target.value)}
                        data-testid="trigger-system-event-select"
                        className="w-full h-9 px-3 rounded-lg border border-slate-200 text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                      >
                        {systemEventOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {systemEventNeedsDays && (
                        <div className="flex items-center gap-2">
                          <span className="text-[12px] text-slate-500">after</span>
                          <input type="number" min="1" value={sysDays} onChange={(e) => setSysDays(e.target.value)} data-testid="trigger-system-event-days" className="w-20 h-8 px-2 rounded-md border border-slate-200 text-[13px] text-slate-900 bg-white" />
                          <span className="text-[12px] text-slate-500">days, if still true</span>
                        </div>
                      )}
                    </div>
                  )}
                </TriggerOption>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[12.5px] font-bold text-slate-800 uppercase tracking-wide">Then (actions)</div>
              <div className="relative">
                <Button onClick={() => setAddOpen((o) => !o)} data-testid="builder-add-action" className="h-8 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[12.5px] gap-1">
                  <Plus className="h-3.5 w-3.5" /> Add action
                </Button>
                {addOpen && (
                  <div className="absolute right-0 mt-1.5 w-52 bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden z-20" data-testid="action-type-menu">
                    {Object.entries(ACTION_DEFS).map(([type, def]) => {
                      const Icon = def.icon;
                      return (
                        <button key={type} onClick={() => addAction(type)} data-testid={`add-action-${type}`} className="w-full px-3 py-2 text-left text-[13px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                          <Icon className="h-3.5 w-3.5" /> {def.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {actions.length === 0 && (
              <div className="text-[12.5px] text-slate-400 text-center py-6 border-2 border-dashed border-slate-200 rounded-lg">
                No actions yet. Add one above.
              </div>
            )}

            <div className="space-y-2.5">
              {actions.map((action, idx) => (
                <ActionCard
                  key={action.id}
                  index={idx}
                  action={action}
                  trigger={trigger}
                  users={users}
                  channels={channels}
                  campaigns={campaigns}
                  savedEmails={savedEmails}
                  onUpdate={(patch) => updateAction(action.id, patch)}
                  onConfig={(patch) => updateConfig(action.id, patch)}
                  onRemove={() => removeAction(action.id)}
                />
              ))}
            </div>
          </div>

          {error && (
            <div data-testid="builder-error" className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-[12.5px] text-rose-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> {error}
            </div>
          )}
        </div>

        <div className="px-6 py-3.5 border-t border-slate-200 bg-white flex items-center justify-end gap-2 sticky bottom-0">
          <Button variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="builder-save" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">
            {busy ? "Saving…" : isEdit ? "Save changes" : "Create automation"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TriggerOption({ active, onClick, title, desc, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left rounded-lg border p-3 transition-colors ${active ? "border-blue-400 bg-blue-50/50 ring-1 ring-blue-200" : "border-slate-200 hover:border-slate-300"}`}
    >
      <div className="flex items-center gap-2">
        <span className={`h-3.5 w-3.5 rounded-full border-2 ${active ? "border-blue-600 bg-blue-600" : "border-slate-300"}`} />
        <span className="text-[13px] font-semibold text-slate-800">{title}</span>
      </div>
      <div className="text-[11.5px] text-slate-500 ml-5.5 mt-0.5 pl-0.5">{desc}</div>
      {children}
    </button>
  );
}

function ActionCard({ index, action, trigger, users, channels, campaigns, savedEmails, onUpdate, onConfig, onRemove }) {
  const def = ACTION_DEFS[action.type] || ACTION_DEFS.send_email;
  const Icon = def.icon;
  const { val, unit } = minutesToParts(action.delayMinutes);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid={`action-card-${index}`}>
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <GripVertical className="h-3.5 w-3.5 text-slate-300" />
          <span className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-2 py-1 rounded-md border ${def.color}`}>
            <Icon className="h-3.5 w-3.5" /> {def.label}
          </span>
        </div>
        <button
          onClick={onRemove}
          data-testid={`action-remove-${index}`}
          title="Remove this action"
          style={{ backgroundColor: "#fef2f2", color: "#dc2626", borderColor: "#fecaca" }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#dc2626"; e.currentTarget.style.color = "#ffffff"; e.currentTarget.style.borderColor = "#dc2626"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#fef2f2"; e.currentTarget.style.color = "#dc2626"; e.currentTarget.style.borderColor = "#fecaca"; }}
          className="h-8 px-2.5 rounded-md border text-[12px] font-semibold flex items-center gap-1.5 shadow-sm transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Remove
        </button>
      </div>

      {/* Timing */}
      {trigger === "demo_booked" ? (
        <div className="flex items-center gap-2 mb-3 text-[12px] text-slate-600 flex-wrap">
          <Clock className="h-3.5 w-3.5 text-slate-400" />
          <select
            value={action.config?.timingAnchor || "trigger"}
            onChange={(e) => onConfig({ timingAnchor: e.target.value })}
            data-testid={`action-anchor-${index}`}
            className="h-8 px-2 rounded-md border border-slate-200 text-[13px] text-slate-900 bg-white"
          >
            <option value="trigger">Right when booked</option>
            <option value="before_meeting">Before the meeting</option>
            <option value="after_meeting">After the meeting</option>
          </select>
          {(action.config?.timingAnchor === "before_meeting" || action.config?.timingAnchor === "after_meeting") && (
            <>
              <input
                type="number"
                min="0"
                value={val}
                onChange={(e) => onUpdate({ delayMinutes: partsToMinutes(e.target.value, unit) })}
                data-testid={`action-delay-val-${index}`}
                className="w-16 h-8 px-2 rounded-md border border-slate-200 text-[13px] text-slate-900 bg-white"
              />
              <select
                value={unit}
                onChange={(e) => onUpdate({ delayMinutes: partsToMinutes(val, e.target.value) })}
                data-testid={`action-delay-unit-${index}`}
                className="h-8 px-2 rounded-md border border-slate-200 text-[13px] text-slate-900 bg-white"
              >
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
              <span>{action.config?.timingAnchor === "before_meeting" ? "before the meeting" : "after the meeting"}</span>
            </>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-3 text-[12px] text-slate-600">
          <Clock className="h-3.5 w-3.5 text-slate-400" />
          <span>Run</span>
          <input
            type="number"
            min="0"
            value={val}
            onChange={(e) => onUpdate({ delayMinutes: partsToMinutes(e.target.value, unit) })}
            data-testid={`action-delay-val-${index}`}
            className="w-16 h-8 px-2 rounded-md border border-slate-200 text-[13px] text-slate-900 bg-white"
          />
          <select
            value={unit}
            onChange={(e) => onUpdate({ delayMinutes: partsToMinutes(val, e.target.value) })}
            data-testid={`action-delay-unit-${index}`}
            className="h-8 px-2 rounded-md border border-slate-200 text-[13px] text-slate-900 bg-white"
          >
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
          <span>after trigger {Number(val) === 0 ? "(immediately)" : ""}</span>
        </div>
      )}

      <ActionConfig action={action} index={index} users={users} channels={channels} campaigns={campaigns} savedEmails={savedEmails} onConfig={onConfig} />
    </div>
  );
}

function ActionConfig({ action, index, users, channels, campaigns, savedEmails, onConfig }) {
  const c = action.config || {};
  if (action.type === "send_email") {
    const srcKey = c.sourceType && c.sourceId ? `${c.sourceType}:${c.sourceId}` : "";
    const selected = (savedEmails || []).find((s) => s.key === srcKey);
    const campaignOpts = (savedEmails || []).filter((s) => s.kind === "campaign");
    const templateOpts = (savedEmails || []).filter((s) => s.kind === "template");
    return (
      <div className="space-y-2">
        <div>
          <label className={labelCls}>Email to send</label>
          <select
            value={srcKey}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return onConfig({ sourceType: "", sourceId: "" });
              const [kind, id] = v.split(":");
              onConfig({ sourceType: kind, sourceId: id });
            }}
            data-testid={`cfg-email-source-${index}`}
            className={inputCls}
          >
            <option value="">✍️  Write a custom message</option>
            {campaignOpts.length > 0 && (
              <optgroup label="Campaigns">
                {campaignOpts.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
              </optgroup>
            )}
            {templateOpts.length > 0 && (
              <optgroup label="Templates">
                {templateOpts.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        {selected ? (
          <>
            {!selected.hasSubject && (
              <FieldWithMergeTags value={c.subject} onChange={(v) => onConfig({ subject: v })} testid={`cfg-email-subject-${index}`} mergeTestid={`merge-email-subject-${index}`} placeholder="Subject (required for this template)" />
            )}
            <div className="text-[11.5px] text-slate-600 bg-blue-50 border border-blue-100 rounded-md px-2.5 py-2 flex items-start gap-1.5" data-testid={`cfg-email-saved-hint-${index}`}>
              <Mail className="h-3.5 w-3.5 mt-0.5 text-blue-600 shrink-0" />
              <span>Sends the latest version of <strong>“{selected.name}”</strong>{selected.hasSubject ? "" : " — add a subject above"}. Edit the email in the Email Builder anytime.</span>
            </div>
          </>
        ) : (
          <>
            <FieldWithMergeTags value={c.subject} onChange={(v) => onConfig({ subject: v })} testid={`cfg-email-subject-${index}`} mergeTestid={`merge-email-subject-${index}`} placeholder="Subject (merge tags ok)" />
            <FieldWithMergeTags value={c.body} onChange={(v) => onConfig({ body: v })} multiline testid={`cfg-email-body-${index}`} mergeTestid={`merge-email-body-${index}`} placeholder="Email body — HTML & {{firstName}} supported" />
            <MergeHint />
          </>
        )}
      </div>
    );
  }
  if (action.type === "send_sms") {
    return (
      <div className="space-y-2">
        <FieldWithMergeTags value={c.message} onChange={(v) => onConfig({ message: v })} multiline rows={2} testid={`cfg-sms-message-${index}`} mergeTestid={`merge-sms-message-${index}`} placeholder="SMS message — {{firstName}} supported" />
        <MergeHint />
      </div>
    );
  }
  if (action.type === "notify_team") {
    const mode = c.mode || "chat";
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelCls}>Notify via</label>
            <select value={mode} onChange={(e) => onConfig({ mode: e.target.value })} data-testid={`cfg-notify-mode-${index}`} className={inputCls}>
              <option value="chat">Internal chat</option>
              <option value="email">Email</option>
              <option value="both">Both</option>
            </select>
          </div>
          {(mode === "chat" || mode === "both") && (
            <div>
              <label className={labelCls}>Channel</label>
              <select value={c.channelId || ""} onChange={(e) => onConfig({ channelId: e.target.value })} data-testid={`cfg-notify-channel-${index}`} className={inputCls}>
                <option value="">#general (default)</option>
                {channels.map((ch) => (
                  <option key={ch.id} value={ch.id}>#{ch.name || ch.id}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        {(mode === "chat" || mode === "both") && (
          <select value={c.userId || ""} onChange={(e) => onConfig({ userId: e.target.value })} data-testid={`cfg-notify-user-${index}`} className={inputCls}>
            <option value="">Mention a user (optional)</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        )}
        {(mode === "email" || mode === "both") && (
          <input value={c.email || ""} onChange={(e) => onConfig({ email: e.target.value })} data-testid={`cfg-notify-email-${index}`} placeholder="Email recipient (blank = all admins)" className={inputCls} />
        )}
        <FieldWithMergeTags value={c.message} onChange={(v) => onConfig({ message: v })} testid={`cfg-notify-message-${index}`} mergeTestid={`merge-notify-message-${index}`} placeholder="Message" />
      </div>
    );
  }
  if (action.type === "set_tag") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>Lead source</label>
          <select value={c.source || ""} onChange={(e) => onConfig({ source: e.target.value })} data-testid={`cfg-tag-source-${index}`} className={inputCls}>
            <option value="">— None —</option>
            {["Google Ads", "Facebook", "Referral", "Website", "Trade Show"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Tags (comma separated)</label>
          <input
            value={Array.isArray(c.tags) ? c.tags.join(", ") : c.tags || ""}
            onChange={(e) => onConfig({ tags: e.target.value })}
            data-testid={`cfg-tag-tags-${index}`}
            placeholder="VIP, Hot"
            className={inputCls}
          />
        </div>
      </div>
    );
  }
  if (action.type === "start_campaign") {
    return (
      <div>
        <label className={labelCls}>Campaign</label>
        <select value={c.campaignId || ""} onChange={(e) => onConfig({ campaignId: e.target.value })} data-testid={`cfg-campaign-${index}`} className={inputCls}>
          <option value="">Select a campaign…</option>
          {campaigns.map((cp) => (
            <option key={cp.id} value={cp.id}>{cp.name}</option>
          ))}
        </select>
      </div>
    );
  }
  if (action.type === "create_task") {
    return (
      <div className="space-y-2">
        <FieldWithMergeTags value={c.title} onChange={(v) => onConfig({ title: v })} testid={`cfg-task-title-${index}`} mergeTestid={`merge-task-title-${index}`} placeholder="Task title — {{customerName}} ok" />
        <div className="grid grid-cols-2 gap-2">
          <select value={c.assigneeId || ""} onChange={(e) => onConfig({ assigneeId: e.target.value })} data-testid={`cfg-task-assignee-${index}`} className={inputCls}>
            <option value="">Assignee (optional)</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
          <input type="number" min="0" value={c.dueInDays ?? ""} onChange={(e) => onConfig({ dueInDays: e.target.value })} data-testid={`cfg-task-due-${index}`} placeholder="Due in N days" className={inputCls} />
        </div>
      </div>
    );
  }
  return null;
}

function MergeHint() {
  return (
    <div className="text-[10.5px] text-slate-400">
      Merge tags: <code>{"{{firstName}}"}</code> <code>{"{{customerName}}"}</code> <code>{"{{scheduledDate}}"}</code> <code>{"{{paymentLink}}"}</code> — see the <span className="font-semibold">{"{ } Tag"}</span> menu for all
    </div>
  );
}

const MERGE_TAG_GROUPS = [
  {
    label: "Customer",
    tags: [
      { tag: "{{firstName}}", label: "First name" },
      { tag: "{{lastName}}", label: "Last name" },
      { tag: "{{customerName}}", label: "Customer name" },
      { tag: "{{customerEmail}}", label: "Customer email" },
      { tag: "{{customerPhone}}", label: "Customer phone" },
      { tag: "{{companyName}}", label: "Company name" },
      { tag: "{{lastServiceDate}}", label: "Last service date" },
    ],
  },
  {
    label: "Appointment",
    tags: [
      { tag: "{{jobId}}", label: "Job ID" },
      { tag: "{{scheduledDate}}", label: "Scheduled date" },
      { tag: "{{scheduledTime}}", label: "Scheduled time" },
      { tag: "{{techName}}", label: "Technician name" },
      { tag: "{{jobAddress}}", label: "Job address" },
      { tag: "{{jobType}}", label: "Job type" },
      { tag: "{{eta}}", label: "Arrival ETA" },
    ],
  },
  {
    label: "Invoice / Payment",
    tags: [
      { tag: "{{paymentLink}}", label: "Payment link" },
      { tag: "{{invoiceNumber}}", label: "Invoice number" },
      { tag: "{{invoiceAmount}}", label: "Invoice amount" },
    ],
  },
  {
    label: "Review",
    tags: [
      { tag: "{{reviewLink}}", label: "Review link" },
    ],
  },
  {
    label: "Demo Booking",
    tags: [
      { tag: "{{bookerName}}", label: "Booker name" },
      { tag: "{{bookerEmail}}", label: "Booker email" },
      { tag: "{{bookedDate}}", label: "Booked date" },
      { tag: "{{bookedTime}}", label: "Booked time" },
      { tag: "{{meetingDuration}}", label: "Meeting duration" },
    ],
  },
];

function MergeTagDropdown({ onPick, testid }) {
  const [open, setOpen] = useState(false);
  const [formsOpen, setFormsOpen] = useState(false);
  const [forms, setForms] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setFormsOpen(false); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const loadForms = async () => {
    if (forms !== null) return;
    try {
      const list = await formsApi.list();
      setForms(list || []);
    } catch {
      setForms([]);
    }
  };

  const pickFormLink = (form) => {
    const origin = (typeof window !== "undefined" && window.location?.origin) || "";
    const url = `${origin}/f/${form.id}`;
    onPick(url);
    setOpen(false);
    setFormsOpen(false);
  };

  return (
    <div className="absolute right-1.5 top-1.5 z-10" ref={ref}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
        data-testid={testid}
        title="Insert merge tag"
        className="inline-flex items-center gap-0.5 h-6 px-1.5 rounded-md border border-slate-200 bg-white text-[10.5px] font-semibold text-slate-500 hover:text-blue-600 hover:border-blue-300 shadow-sm"
      >
        <Braces className="h-3 w-3" /> Tag <ChevronDown className="h-2.5 w-2.5" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 max-h-96 overflow-y-auto bg-white rounded-lg border border-slate-200 shadow-lg py-1" data-testid={`${testid}-menu`}>
          {MERGE_TAG_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">{group.label}</div>
              {group.tags.map((m) => (
                <button
                  key={m.tag}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { onPick(m.tag); setOpen(false); }}
                  data-testid={`${testid}-${m.tag.replace(/[{}]/g, "")}`}
                  className="w-full px-3 py-1.5 text-left hover:bg-slate-50 flex items-center justify-between"
                >
                  <span className="text-[12px] text-slate-700">{m.label}</span>
                  <code className="text-[10px] text-slate-400">{m.tag}</code>
                </button>
              ))}
            </div>
          ))}
          {/* Forms submenu */}
          <div className="border-t border-slate-100 mt-1 pt-1">
            <div className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">Saved forms</div>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setFormsOpen((v) => !v); loadForms(); }}
              data-testid={`${testid}-forms-toggle`}
              className="w-full px-3 py-1.5 text-left hover:bg-slate-50 flex items-center justify-between"
            >
              <span className="text-[12px] text-slate-700">Insert form link…</span>
              <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${formsOpen ? "rotate-180" : ""}`} />
            </button>
            {formsOpen && (
              <div className="max-h-40 overflow-y-auto border-t border-slate-100 bg-slate-50/60" data-testid={`${testid}-forms-list`}>
                {forms === null ? (
                  <div className="px-3 py-2 text-[11.5px] text-slate-400">Loading forms…</div>
                ) : forms.length === 0 ? (
                  <div className="px-3 py-2 text-[11.5px] text-slate-400 italic">No saved forms yet. Create one on the Marketing page.</div>
                ) : (
                  forms.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pickFormLink(f)}
                      data-testid={`${testid}-form-${f.id}`}
                      className="w-full px-3 py-1.5 text-left hover:bg-white flex items-center justify-between"
                    >
                      <span className="text-[12px] text-slate-700 truncate mr-2">{f.title || f.name || f.id}</span>
                      <span className="text-[10px] text-slate-400 font-mono shrink-0">/f/{f.id.slice(0, 6)}…</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FieldWithMergeTags({ value, onChange, multiline, rows = 3, testid, mergeTestid, className = "", ...props }) {
  const ref = useRef(null);
  const insert = (tag) => {
    const el = ref.current;
    const v = value || "";
    const start = el?.selectionStart ?? v.length;
    const end = el?.selectionEnd ?? v.length;
    const next = v.slice(0, start) + tag + v.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + tag.length;
      el.setSelectionRange(pos, pos);
    });
  };
  const base = inputCls + " pr-16 " + className;
  return (
    <div className="relative">
      {multiline ? (
        <textarea ref={ref} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid} className={base + " h-auto py-2 resize-y"} {...props} />
      ) : (
        <input ref={ref} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={base} {...props} />
      )}
      <MergeTagDropdown onPick={insert} testid={mergeTestid} />
    </div>
  );
}

function defaultConfig(type) {
  switch (type) {
    case "send_email": return { subject: "", body: "" };
    case "send_sms": return { message: "" };
    case "notify_team": return { mode: "chat", message: "" };
    case "set_tag": return { source: "", tags: "" };
    case "start_campaign": return { campaignId: "" };
    case "create_task": return { title: "", dueInDays: 1 };
    default: return {};
  }
}

function normalizeConfig(type, c = {}) {
  if (type === "set_tag") {
    const tags = Array.isArray(c.tags) ? c.tags : String(c.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
    return { source: c.source || "", tags };
  }
  if (type === "create_task") {
    return { ...c, dueInDays: c.dueInDays === "" || c.dueInDays == null ? null : Number(c.dueInDays) };
  }
  return c;
}
